import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { routeDelaysManager } from "@/setup/routes/delay.routes";
import {
	dxtToLocalServerTime,
	ErrorModal,
	useContextMenuMESF,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { CircularProgress, Grid2, Paper } from "@mui/material";
import type { EChartsType } from "echarts";
import ReactECharts from "echarts-for-react";
import moment from "moment";
import "moment-duration-format";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { assemblyStatusCodes } from "../../carriers/components/CarrierAssemblyStatusTag";
import { PassChangeModal } from "../../carriers/components/passes/PassChangeModal";
import {
	Adjustment,
	CarrierInstance,
	Cobble,
	Delay,
	DmhEvent,
	PassChange,
	Production,
	RollChange,
	SectionProductionEventStats,
} from "../models/GanttReponse";
import { useGanttOptionsFunctions } from "./hooks/useGanttOptionsFunctions";
import { CarrierInstallationModal } from "./modals/CarrierInstallationModal";
import { DeleteModal } from "./modals/DeleteModal";
import { routeCobblesManager } from "@/setup/routes/mill.routes";
import { eventTypes } from "@/shared/selectors/EventTypeSelector";
import { getColors, getReadableTextColor } from "@/shared/utils/ganttColors";
import {
	compareNonProductionStack,
	isNonProductionEventType,
	NON_PRODUCTION_EVENT_TYPE_CODES,
	NonProductionStackItem,
} from "@/shared/utils/nonProductionEvents";

// ── Shared Gantt bar stroke color (used by Events + Delays rows) ──
const GANTT_BAR_STROKE = "#666";
const DELAY_FALLBACK_COLOR = "#3d556f";
const EVENT_LEGEND_LABEL_SERIES = "__gantt_event_legend_label__";
const LEGEND_SECTION_SPACER_SERIES = "__gantt_legend_section_spacer__";
const DELAY_LEGEND_LABEL_SERIES = "__gantt_delay_legend_label__";
const DELAY_SERIES_PREFIX = "__gantt_delay_category__";
const LEGEND_ITEM_GAP = 12;
const STRUCTURAL_LEGEND_SERIES = new Set([
	EVENT_LEGEND_LABEL_SERIES,
	LEGEND_SECTION_SPACER_SERIES,
	DELAY_LEGEND_LABEL_SERIES,
]);

type DelaySeriesName = `${typeof DELAY_SERIES_PREFIX}${number}`;

interface LegendSelectChangedParams {
	name: string;
	selected: Record<string, boolean>;
}

const keepStructuralLegendSeriesSelected = (
	params: LegendSelectChangedParams,
	chartInstance: EChartsType,
) => {
	if (
		STRUCTURAL_LEGEND_SERIES.has(params.name) &&
		!params.selected[params.name]
	) {
		chartInstance.dispatchAction(
			{ type: "legendSelect", name: params.name },
			{ silent: true },
		);
	}
};

const GANTT_CHART_EVENTS = {
	legendselectchanged: keepStructuralLegendSeriesSelected,
};

interface DelayCategory {
	name: string;
	color: string;
}

const getDelaySeriesName = (index: number): DelaySeriesName => {
	return `${DELAY_SERIES_PREFIX}${index}`;
};

const normalizeDelayCategory = (delay: Delay): DelayCategory => {
	return {
		name: delay.DelayCategoryName?.trim() || "Undeclared",
		color: delay.DelayCategoryColor?.trim() || DELAY_FALLBACK_COLOR,
	};
};

// ── D/M/H color mapping derived from the single source of truth (eventTypes) ──
const DMH_COLORS: Record<
	string,
	{ color: string; bg: string; text: string; name: string }
> = Object.fromEntries(
	eventTypes
		.filter((et) => isNonProductionEventType(et.code))
		.map((et) => [
			et.code,
			{
				color: et.borderColor,
				bg: et.backgroundColor,
				text: getReadableTextColor(et.borderColor, 7, 45),
				name: et.name,
			},
		]),
);

// ── EventSegment: unified block for the merged Events row ────────────
interface EventSegment {
	type: "rc" | "dmh";
	start: Date;
	end: Date;
	color: string;
	strokeColor: string;
	label: string;
	// RC-specific (preserved for tooltip/interaction)
	rcData?: {
		prevSectionName: string;
		nextSectionName: string;
		scheduledHours: number;
		actualHours: number | null;
		trackedHours: number | null;
		effectiveActualHours: number | null;
		isActualEdited: boolean;
		isScheduled: boolean;
		isInProgress: boolean;
		isWarning: boolean;
		isOverdue: boolean;
		isOverSchedule: boolean;
	};
	// State flags propagated to item for per-series filtering and legend
	isInProgress?: boolean;
	isOverdue?: boolean;
	isWarning?: boolean;
	isOverSchedule?: boolean;
	// D/M/H-specific
	eventId?: number;
	eventTypeCode?: string;
	eventTypeName?: string;
	comments?: string | null;
	createdAt?: Date | null;
	/** Original End from the event (before clipping to now). Null = open-ended. */
	originalEnd?: Date | null;
}

/**
 * Merge RC blocks and D/M/H events into a single sorted EventSegment[].
 *
 * D/M/H blocks render at their full extent and take visual priority.
 * RC blocks are split/trimmed around overlapping D/M/H events.
 * Pure function, no side effects.
 */
function mergeEventsForRow(
	rcBlocks: {
		value: (number | Date | null)[];
		prevSectionName: string;
		nextSectionName: string;
		scheduledHours: number;
		actualHours: number | null;
		trackedHours: number | null;
		effectiveActualHours: number | null;
		isActualEdited: boolean;
		isScheduled: boolean;
		isInProgress: boolean;
		isWarning: boolean;
		isOverdue: boolean;
		isOverSchedule: boolean;
		color: string;
	}[],
	dmhEvents: DmhEvent[],
	now: Date,
): EventSegment[] {
	const segments: EventSegment[] = [];

	// Normalize D/M/H events: null End → now, future End → clipped to now
	const normalizedDmh = dmhEvents
		.filter((e) => e.Start != null)
		.map((e) => {
			const end = e.End ? new Date(e.End) : now;
			return {
				...e,
				start: new Date(e.Start!),
				end: end > now ? now : end,
			};
		});

	// 1. Add all D/M/H events at their full extent
	for (const dmh of normalizedDmh) {
		const dmhColors = DMH_COLORS[dmh.EventTypeCode] ?? DMH_COLORS.D;
		segments.push({
			type: "dmh",
			start: dmh.start,
			end: dmh.end,
			color: dmhColors.color,
			strokeColor: dmhColors.color,
			label: dmhColors.name,
			eventId: dmh.EventId,
			eventTypeCode: dmh.EventTypeCode,
			eventTypeName: dmhColors.name,
			comments: dmh.Comments,
			createdAt: dmh.CreatedAt ? new Date(dmh.CreatedAt) : null,
			originalEnd: dmh.End ? new Date(dmh.End) : null,
		});
	}

	// 2. For each RC, split around overlapping D/M/H and add remaining RC segments
	for (const rc of rcBlocks) {
		const rcStart = new Date(rc.value[1] as Date);
		const rcEnd = new Date(rc.value[2] as Date);

		// RC stroke color: green when on time, red when over schedule
		const strokeColor = rc.isOverSchedule ? "#D32F2F" : "#388E3C";

		const rcData = {
			prevSectionName: rc.prevSectionName,
			nextSectionName: rc.nextSectionName,
			scheduledHours: rc.scheduledHours,
			actualHours: rc.actualHours,
			trackedHours: rc.trackedHours,
			effectiveActualHours: rc.effectiveActualHours,
			isActualEdited: rc.isActualEdited,
			isScheduled: rc.isScheduled,
			isInProgress: rc.isInProgress,
			isWarning: rc.isWarning,
			isOverdue: rc.isOverdue,
			isOverSchedule: rc.isOverSchedule,
		};
		const rcFlags = {
			isInProgress: rc.isInProgress,
			isOverdue: rc.isOverdue,
			isWarning: rc.isWarning,
			isOverSchedule: rc.isOverSchedule,
		};

		const rcLabel = `${rc.prevSectionName} → ${rc.nextSectionName}`;

		// Find overlapping D/M/H events (dmh.start < rc.end AND dmh.end > rc.start)
		const overlapping = normalizedDmh
			.filter(
				(d) =>
					d.start.getTime() < rcEnd.getTime() &&
					d.end.getTime() > rcStart.getTime(),
			)
			.sort((a, b) => a.start.getTime() - b.start.getTime());

		if (overlapping.length === 0) {
			// No overlap — RC renders intact
			segments.push({
				type: "rc",
				start: rcStart,
				end: rcEnd,
				color: rc.color,
				strokeColor,
				label: rcLabel,
				rcData,
				...rcFlags,
			});
			continue;
		}

		// Walk through RC timeline, carving out D/M/H overlap regions
		let cursor = rcStart.getTime();

		for (const dmh of overlapping) {
			// The portion of D/M/H that actually overlaps the RC
			const overlapStart = Math.max(dmh.start.getTime(), rcStart.getTime());
			const overlapEnd = Math.min(dmh.end.getTime(), rcEnd.getTime());

			// RC segment before this D/M/H overlap (skip if zero-width)
			if (cursor < overlapStart) {
				segments.push({
					type: "rc",
					start: new Date(cursor),
					end: new Date(overlapStart),
					color: rc.color,
					strokeColor,
					label: rcLabel,
					rcData,
					...rcFlags,
				});
			}

			// Jump cursor past the overlap
			cursor = Math.max(cursor, overlapEnd);
		}

		// Remaining RC after last D/M/H overlap
		if (cursor < rcEnd.getTime()) {
			segments.push({
				type: "rc",
				start: new Date(cursor),
				end: rcEnd,
				color: rc.color,
				strokeColor,
				label: rcLabel,
				rcData,
				...rcFlags,
			});
		}
	}

	// Sort by start time
	segments.sort((a, b) => a.start.getTime() - b.start.getTime());

	return segments;
}

interface SectionGroup {
	sectionId: number;
	sectionName: string;
	billets: unknown[];
	minStart: Date;
	maxEnd: Date;
	totalDurationSecs: number;
}

interface sectionRow {
	value: [number, Date, Date, number, string];
	sectionName: string;
	sectionId: number;
	duration: number;
	activeTimeCurrentShiftSecs: number;
	rollingStart: Date | null;
	rollTimeSecs: number | null;
	sectionSpansMultipleShifts: boolean | null;
}
export interface installData {
	clickedTime?: number;
	installedat?: Date | null;
	uninstalledat?: Date | null;
	partId?: number;
	partName?: string;
	rollsetId?: number;
	carrierInstanceId?: number;
	activeSection: string | null;
	activeSectionId: number | null;
	activeStand: string | null;
	activeStandId: number;
}

type CarrierData = {
	start: Date;
	end: Date;
	partId: number;
	partName: string;
	rollsetId: number;
	carrierInstanceId: number;
	sectionName: string;
	sectionId: number;
	standName: string;
	standId: number;
};

type PassChangeData = {
	timestamp: string;
	carrierName: string;
	carrierInstanceId: number;
	sectionName: string;
	standName: string;
};

type DelayData = {
	shiftId: number;
	delayId: number;
	isDelay: true;
};

function isDelayData(data: unknown): data is DelayData {
	return (
		typeof data === "object" &&
		data !== null &&
		"isDelay" in data &&
		data.isDelay === true
	);
}

type CobbleData = {
	shiftId: number;
	cobbleId: number;
};

type ChartClickParams =
	| { seriesName: "Carriers"; data: CarrierData }
	| { seriesName: "Pass Changes"; data: PassChangeData }
	| { seriesName: DelaySeriesName; data: DelayData }
	| { seriesName: "Cobbles"; data: CobbleData };

const standsIds: Record<string, number> = {
	F1: 10,
	F2: 11,
	F3: 12,
	F4: 13,
	F5: 14,
	F6: 15,
	F7: 16,
	R1: 17,
	R2: 18,
	R3: 19,
	R4: 20,
};

export interface GapTimeData {
	Start: Date | null;
	End: Date | null;
	Duration: number;
}

interface Props {
	isLoading: boolean;
	refetch: () => void;
	start: Date | undefined;
	end: Date | undefined;
	productionData: Production[];
	delayData: Delay[];
	gapTimeData?: GapTimeData[];
	adjustmentData?: Adjustment[];
	carrierRealData?: CarrierInstance[];
	cobbleData?: Cobble[];
	passChangeData?: PassChange[];
	rollChangeData?: RollChange[];
	dmhEventData?: DmhEvent[];
	setIsModalOpen?: (open: boolean) => void;
	compactMode?: boolean;
	onHeatHover?: (heatId: number | null) => void;
	hoveredHeatId?: number | null;
	onDelayHover?: (delayId: number | null) => void;
	hoveredDelayId?: number | null;
	onSectionHover?: (sectionId: number | null) => void;
	hoveredSectionId?: number | null;
	highlightedCarrierInstanceId?: number | null;
	sectionEventStatsData?: SectionProductionEventStats[];
}

export const GanttChart = ({
	isLoading,
	refetch,
	start,
	end,
	productionData,
	delayData,
	gapTimeData = [],
	adjustmentData = [],
	carrierRealData = [],
	cobbleData = [],
	passChangeData = [],
	rollChangeData = [],
	dmhEventData = [],
	setIsModalOpen = () => {},
	compactMode = false,
	onHeatHover,
	hoveredHeatId,
	onDelayHover,
	hoveredDelayId,
	onSectionHover,
	hoveredSectionId,
	highlightedCarrierInstanceId,
	sectionEventStatsData = [],
}: Props) => {
	const { shiftId: shiftIdParam } = useParams();
	const { state: settingsState } = useUTLSettingsContext();

	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [dataToInstallCarrier, setDataToInstallCarrier] =
		useState<installData | null>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const [openPassChangeDialog, setOpenPassChangeDialog] = useState(false);
	const [notificationMessage, setNotificationMessage] = useState("");

	const TIME_START = start?.toISOString(); // Chart start time
	const TIME_END = end?.toISOString(); // Chart end time

	const { registerConfig, showContextMenu } = useContextMenuMESF();
	const { getMenuOptions } = useGanttOptionsFunctions({
		setDataToInstallCarrier,
		setShowConfirmDialog,
		setIsModalOpen,
		setOpenPassChangeDialog,
	});

	const getMenuOptionsRef = useRef(getMenuOptions);
	const showContextMenuRef = useRef(showContextMenu);
	const chartRef = useRef<any>(null);
	const sectionsRef = useRef<sectionRow[]>([]);

	const hasGapTime = compactMode && gapTimeData.length > 0;

	const stands = compactMode
		? hasGapTime
			? ["", "", "", "Production", "", "Events", "Delays", "Gap Time"]
			: ["", "", "", "Production", "", "Events", "Delays"]
		: [
				"",
				"",
				"",
				"",
				"Production",
				"Sections",
				"Events",
				"Delays",
				"R1",
				"R2",
				"R3",
				"R4",
				"F1",
				"F2",
				"F3",
				"F4",
				"F5",
				"F6",
				"F7",
			];

	const PRODUCTION_ROW = compactMode ? 3 : 4;
	const EVENTS_ROW = compactMode ? 5 : 6;
	const DELAYS_ROW = compactMode ? 6 : 7;

	const onChartReady = (chartInstance: any) => {
		chartRef.current = chartInstance;

		chartInstance.on("contextmenu", (params: any) => {
			params.event.event.preventDefault();
			if (isDelayData(params.data)) {
				showContextMenuRef.current(event as any, params.data, "ganttContext");
				return;
			}
			switch (params.seriesName) {
				case "Billets":
					// future
					break;
				case "Carriers":
					// Calculate clicked time from mouse position
					const carrierEvent = params.event.event;
					const carrierRect = chartRef.current.getDom().getBoundingClientRect();
					const carrierX = carrierEvent.clientX - carrierRect.left;
					const carrierY = carrierEvent.clientY - carrierRect.top;
					const carrierPointInGrid = chartRef.current.convertFromPixel("grid", [
						carrierX,
						carrierY,
					]);
					const carrierClickedTime = carrierPointInGrid
						? carrierPointInGrid[0]
						: undefined;

					showContextMenuRef.current(
						event as any,
						{ ...params.data, clickedTime: carrierClickedTime },
						"ganttContext",
					);
					break;
				case "Cobbles":
					showContextMenuRef.current(event as any, params.data, "ganttContext");
					break;
				case "Adjustments":
					showContextMenuRef.current(event as any, params.data, "ganttContext");
					break;
				case "Pass Changes":
					showContextMenuRef.current(event as any, params.data, "ganttContext");
					break;
				case "Sections":
					// future
					break;
			}
		});

		chartInstance.on("click", (params: ChartClickParams) => {
			if (isDelayData(params.data)) {
				window.open(
					`${routeDelaysManager}/${params.data.shiftId}?assetId=12&delayId=${params.data.delayId}`,
					"_blank",
				);
			} else if (params.seriesName === "Carriers") {
				setDataToInstallCarrier({
					installedat: params.data.start,
					uninstalledat: params.data.end,
					partId: params.data.partId,
					partName: params.data.partName,
					rollsetId: params.data.rollsetId,
					carrierInstanceId: params.data.carrierInstanceId,
					activeSection: params.data.sectionName,
					activeSectionId: params.data.sectionId,
					activeStand: params.data.standName,
					activeStandId: params.data.standId,
				});
			} else if (params.seriesName === "Pass Changes") {
				const carrier = carrierRealData.find(
					(c) => c.CarrierInstanceId === params.data.carrierInstanceId,
				);
				setDataToInstallCarrier({
					clickedTime: new Date(params.data.timestamp).getTime(),
					partId: carrier?.PartId,
					partName: params.data.carrierName,
					carrierInstanceId: params.data.carrierInstanceId,
					activeSection: params.data.sectionName,
					activeSectionId: null,
					activeStand: params.data.standName,
					activeStandId: 0,
				});
				setOpenPassChangeDialog(true);
			} else if (params.seriesName === "Cobbles") {
				window.open(
					`${routeCobblesManager}/${params.data.shiftId}?cobbleId=${params.data.cobbleId}`,
					"_blank",
				);
			}
		});

		chartInstance.on("mouseover", (params: any) => {
			if (params.seriesName === "Billets" && onHeatHover) {
				const heatId = params.data.value[7];
				onHeatHover(heatId);
			}
			if (isDelayData(params.data) && onDelayHover) {
				onDelayHover(params.data.delayId);
			}
			if (params.seriesName === "Sections" && onSectionHover) {
				onSectionHover(params.data.sectionId);
			}
		});

		chartInstance.on("mouseout", (params: any) => {
			if (params.seriesName === "Billets" && onHeatHover) {
				onHeatHover(null);
			}
			if (isDelayData(params.data) && onDelayHover) {
				onDelayHover(null);
			}
			if (params.seriesName === "Sections" && onSectionHover) {
				onSectionHover(null);
			}
		});

		const chartDom = chartInstance.getDom();

		const handleMouseMove = (event: MouseEvent) => {
			const rect = chartDom.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const y = event.clientY - rect.top;

			const pointInGrid = chartInstance.convertFromPixel("grid", [x, y]);

			if (pointInGrid) {
				const rowIndex = Math.round(pointInGrid[1]);
				if (rowIndex >= 0 && rowIndex < stands.length) {
					setHoveredRowIndex(rowIndex);
				} else {
					setHoveredRowIndex(null);
				}
			} else {
				setHoveredRowIndex(null);
			}
		};

		const handleMouseLeave = () => {
			setHoveredRowIndex(null);
		};

		chartDom.addEventListener("mousemove", handleMouseMove);
		chartDom.addEventListener("mouseleave", handleMouseLeave);

		if (!compactMode) {
			chartDom.addEventListener("contextmenu", (event: MouseEvent) => {
				const rect = chartDom.getBoundingClientRect();
				const x = event.clientX - rect.left;
				const y = event.clientY - rect.top;

				// Convert pixel coordinates to chart coordinates
				const pointInGrid = chartInstance.convertFromPixel("grid", [x, y]);

				if (pointInGrid && pointInGrid[1] < stands.length) {
					const timeValue = pointInGrid[0];
					const rowIndex = Math.round(pointInGrid[1]);
					const stand = stands[rowIndex];

					// Find the active section at the clicked time
					const clickedTime = new Date(timeValue);
					const activeSection: sectionRow | undefined =
						sectionsRef.current.find((section: { value: any }) => {
							const sectionStart = new Date(section.value[1]);
							const sectionEnd = new Date(section.value[2]);
							return clickedTime >= sectionStart && clickedTime <= sectionEnd;
						});

					if (rowIndex > DELAYS_ROW) {
						showContextMenuRef.current(
							event as any,
							{
								clickedTime: timeValue,
								activeStand: stand,
								activeStandId: standsIds[stand],
								activeSection: activeSection?.sectionName ?? null,
								activeSectionId: activeSection?.sectionId ?? null,
							},
							"ganttContext",
						);
					}
				}
			});
		}
	};

	// Helper function to get stand index
	function getStandIndex(standName: string | undefined) {
		if (!standName) return 0;
		const index = stands.indexOf(standName);
		return index >= 0 ? index : 0;
	}

	// Function to extract carrier information from nested JSON structure
	function extractCarrierInfo(carrier: CarrierInstance) {
		// Navigate through the nested structure to get information
		const part = {
			PartId: carrier.PartId,
			PartName: carrier.PartName,
			CarrierInstanceId: carrier.CarrierInstanceId,
		};
		const rollset = {
			RollsetId: carrier.RollsetId,
			TopRollId: carrier.TopRollId,
			TopRollName: carrier.TopRollName,
			BottomRollId: carrier.BottomRollId,
			BottomRollName: carrier.BottomRollName,
		};
		const standInfo = {
			StandId: carrier.StandId,
			StandName: carrier.StandName,
			OperatorName: carrier.OperatorName,
		};
		const sectionInfo = {
			SectionId: carrier.SectionId,
			SectionName: carrier.SectionName,
		};
		const campaignInfo = {
			CarrierCampaignWeight: carrier.CarrierCampaignWeight ?? 0,
			CarrierCampaignOffset: carrier.CarrierOffsetWeight ?? 0,
			TopRollCampaignWeight: carrier.TopRollCampaignWeight ?? 0,
			TopRollOffsetWeight: carrier.TopRollOffsetWeight ?? 0,
			TopRollAccumulatedWeight:
				(carrier.TopRollCampaignWeight ?? 0) +
				(carrier.TopRollStartWeight ?? 0) +
				(carrier.TopRollOffsetWeight ?? 0),
			TopRollCampaignOffset: carrier.TopRollCampaignWeight ?? 0,
			BottomRollCampaignWeight: carrier.BottomRollCampaignWeight ?? 0,
			BottomRollCampaignOffset: carrier.BottomRollCampaignWeight ?? 0,
			BottomRollAccumulatedWeight:
				(carrier.BottomRollCampaignWeight ?? 0) +
				(carrier.BottomRollStartWeight ?? 0) +
				(carrier.TopRollOffsetWeight ?? 0),
		};

		const topRollSelectedPass =
			carrier.TopRollSelectedPassNumber != null
				? `${carrier.TopRollSelectedPassNumber}°`
				: "";
		const topRollSelectedPassAccumulatedTons =
			carrier.TopRollSelectedPassAccumulatedTons != null
				? carrier.TopRollSelectedPassAccumulatedTons
				: 0;
		const bottomRollSelectedPass =
			carrier.BottomRollSelectedPassNumber != null
				? `${carrier.BottomRollSelectedPassNumber}°`
				: "";
		const bottomRollSelectedPassAccumulatedTons =
			carrier.BottomRollSelectedPassAccumulatedTons != null
				? carrier.BottomRollSelectedPassAccumulatedTons
				: 0;

		return {
			partId: part.PartId,
			partName: part.PartName,
			carrierInstanceId: carrier.CarrierInstanceId,
			sectionName: sectionInfo.SectionName,
			sectionId: sectionInfo.SectionId,
			standName: standInfo.StandName,
			standId: standInfo.StandId,
			standIndex: getStandIndex(standInfo.StandName),
			rollsetId: rollset.RollsetId,
			start: carrier.InstalledAt,
			end: carrier.UninstalledAt,
			carrierLabelBar: `TR: ${
				rollset.TopRollName
			} (${parseFloat(campaignInfo.TopRollAccumulatedWeight.toFixed(2) || "0")} tn) - BR: ${
				rollset.BottomRollName
			} (${parseFloat(campaignInfo.BottomRollAccumulatedWeight.toFixed(2) || "0")} tn)`,
			campaignWeight: campaignInfo.CarrierCampaignWeight,
			topRollId: rollset.TopRollId,
			topRollName: rollset.TopRollName,
			topRollPass: topRollSelectedPass,
			bottomRollId: rollset.BottomRollId,
			bottomRollName: rollset.BottomRollName,
			bottomRollPass: bottomRollSelectedPass,
			topRollAccumulatedWeight: campaignInfo.TopRollAccumulatedWeight,
			bottomRollAccumulatedWeight: campaignInfo.BottomRollAccumulatedWeight,
			topRollSelectedPassAccumulatedTons: parseFloat(
				topRollSelectedPassAccumulatedTons.toFixed(2) || "0",
			),
			bottomRollSelectedPassAccumulatedTons: parseFloat(
				bottomRollSelectedPassAccumulatedTons.toFixed(2) || "0",
			),
			operatorName: standInfo.OperatorName,
			carrierStatus: carrier.CarrierStatusCode,
			bait: carrier.Bait,
		};
	}

	// Process real carrier data
	const carrierData = compactMode
		? []
		: carrierRealData
				.filter((c) => c.StandId)
				.map((carrier) => extractCarrierInfo(carrier));

	// Prepare data for chart series
	const billetsData = productionData
		.filter((d) => d.EventTypeCode === "B")
		.map((d) => ({
			value: [
				PRODUCTION_ROW,
				d.Start,
				d.End,
				d.Billets,
				d.Weight,
				d.TPH,
				d.SectionName,
				d.HeatId,
				d.HeatName,
			],
			billets: d.Billets,
			weight: d.Weight,
			pushRate: d.PushRate,
			tph: d.TPH,
			sectionName: d.SectionName,
			sectionId: d.SectionId,
			heatName: d.HeatName,
			shiftId: Number(shiftIdParam),
			billetId: d.BilletId,
			duration: d.Duration,
		}));

	const sectionsGrouped = billetsData.reduce(
		(acc, item) => {
			const sectionId = item.sectionId;

			const startTime = item.value[1] as Date;
			const endTime = item.value[2] as Date;

			if (!startTime || !endTime) return acc;

			if (!acc[sectionId]) {
				acc[sectionId] = {
					sectionId,
					sectionName: item.sectionName,
					billets: [],
					minStart: startTime,
					maxEnd: endTime,
					totalDurationSecs: 0,
				};
			}

			acc[sectionId].billets.push(item);
			acc[sectionId].totalDurationSecs += item.duration ?? 0;

			if (startTime < acc[sectionId].minStart) {
				acc[sectionId].minStart = startTime;
			}
			if (endTime > acc[sectionId].maxEnd) {
				acc[sectionId].maxEnd = endTime;
			}

			return acc;
		},
		{} as Record<number, SectionGroup>,
	);

	const sectionsRowData: sectionRow[] = Object.values(sectionsGrouped).map(
		(section) => {
			const eventStats = sectionEventStatsData.find(
				(s) => s.SectionId === section.sectionId,
			);
			return {
				value: [
					PRODUCTION_ROW,
					section.minStart,
					section.maxEnd,
					section.sectionId,
					section.sectionName,
				],
				sectionName: section.sectionName,
				sectionId: section.sectionId,
				duration: section.maxEnd.getTime() - section.minStart.getTime(),
				activeTimeCurrentShiftSecs: section.totalDurationSecs,
				rollingStart: eventStats?.RollingStart ?? null,
				rollTimeSecs: eventStats?.RollTimeSecs ?? null,
				sectionSpansMultipleShifts:
					eventStats?.SectionSpansMultipleShifts ?? null,
			};
		},
	);

	sectionsRef.current = sectionsRowData;

	const cobblesData = compactMode
		? []
		: cobbleData.map((c: Cobble) => {
				const standIndex = getStandIndex(c.LastStandName);
				return {
					value: [standIndex, c.Start, c.End],
					cobbles: 1,
					lastStand: c.LastStandName,
					shiftId: Number(shiftIdParam),
					cobbleId: c.BilletId,
				};
			});

	const GAP_TIME_ROW = hasGapTime ? 7 : -1;

	const delaysData = delayData.map((d: Delay) => {
		const category = normalizeDelayCategory(d);
		const categoryColor = d.DelayCategoryColor?.trim() || undefined;
		return {
			value: [DELAYS_ROW, d.Start, d.End || new Date()],
			end: d.End,
			delayName: d.DelayCodeName,
			category: category.name,
			delayId: d.DelayId,
			shiftId: Number(shiftIdParam),
			comments: d.Comments,
			color: category.color,
			categoryColor,
			isDelay: true as const,
		};
	});
	const delayCategories = Array.from(
		delaysData
			.reduce(
				(categories, delay) => {
					const category = categories.get(delay.category) ?? {
						name: delay.category,
						legendColor: undefined,
						data: [],
					};
					category.legendColor ??= delay.categoryColor;
					category.data.push(delay);
					categories.set(delay.category, category);
					return categories;
				},
				new Map<
					string,
					{
						name: string;
						legendColor: string | undefined;
						data: typeof delaysData;
					}
				>(),
			)
			.values(),
	).map((category, index) => ({
		...category,
		color: category.legendColor ?? DELAY_FALLBACK_COLOR,
		seriesName: getDelaySeriesName(index),
	}));
	const delayLegendLabels = new Map<string, string>(
		delayCategories.map((category) => [category.seriesName, category.name]),
	);

	const minGapTimeDisplaySeconds = Number(
		get(settingsState, "settings.MIN_GAP_TIME_DISPLAY_SECONDS", 180),
	);
	const gapTimeDataFormatted = hasGapTime
		? gapTimeData
				.filter((dt) => dt.Duration >= minGapTimeDisplaySeconds)
				.map((dt) => ({
					value: [GAP_TIME_ROW, dt.Start, dt.End || new Date()],
					duration: dt.Duration,
				}))
		: [];

	const adjustmentsData = compactMode
		? []
		: adjustmentData.map((adj: Adjustment) => {
				const standIndex = getStandIndex(adj.StandName);
				return {
					value: [standIndex, adj.Timestamp],
					adjustmentType: adj.AdjustmentTypeName,
					adjustmentReason: adj.AdjustmentReasonName,
					adjustmentValue: `${adj.AdjustmentValue} ${adj.Unit}`,
					standName: adj.StandName,
					operatorName: adj.OperatorName,
					timestamp: adj.Timestamp,
					adjustmentId: adj.MillAdjustmentId,
					shiftId: Number(shiftIdParam),
				};
			});

	const passChangesData = compactMode
		? []
		: passChangeData.map((cc: PassChange) => {
				const standIndex = getStandIndex(cc.StandName);
				return {
					value: [standIndex, cc.Timestamp, cc.RollPosition],
					passChangeId: cc.CarrierInstancePassId,
					carrierInstanceId: cc.CarrierInstanceId,
					rollName: cc.RollName,
					rollPosition: cc.RollPosition === "T" ? "Top" : "Bottom",
					passNumber: cc.PassNumber,
					prevPassNumber: cc.PrevPassNumber,
					standName: cc.StandName,
					sectionName: cc.SectionName,
					carrierName: cc.CarrierName,
					timestamp: cc.Timestamp,
					shiftId: Number(shiftIdParam),
				};
			});

	const rollChangesData = rollChangeData.map((rc: RollChange) => {
		const isScheduled = rc.StatusCode === "P";
		const isInProgress = !isScheduled && rc.End === null;
		const isCompleted = !isScheduled && !isInProgress;
		const isActualEdited =
			rc.IsActualRollChangeEdited === true || rc.IsActualRollChangeEdited === 1;
		const trackedHours = rc.TrackedRollChangeHours ?? null;
		const effectiveActualHours =
			rc.EffectiveActualRollChangeHours ?? rc.ActualRollChangeHours;
		const isOverSchedule =
			isCompleted &&
			!!rc.ScheduledRollChangeHours &&
			effectiveActualHours != null &&
			effectiveActualHours > rc.ScheduledRollChangeHours;

		// For in-progress: use server-calculated RemainingMinutes (includes D/M/H exclusion)
		const remainingMinutes = isInProgress ? (rc.RemainingMinutes ?? 0) : 0;
		const isOverdue = isInProgress && remainingMinutes < 0;
		const warningThreshold = rc.WarningThresholdMinutes ?? 10;
		const isWarning =
			isInProgress && !isOverdue && remainingMinutes <= warningThreshold;

		// Colors: lighter solid tones to distinguish from Delays
		const color = isScheduled
			? "#2196F3"
			: isOverSchedule || isOverdue
				? "#FFCDD2" // Material Red 100
				: isWarning
					? "#FFE0B2" // Material Orange 100
					: "#24DA1E"; // Neon green
		// Block end: for scheduled use Start + ScheduledHours, for in-progress use now, for completed use End
		const blockEnd = isScheduled
			? new Date(
					new Date(rc.Start!).getTime() +
						rc.ScheduledRollChangeHours * 3600_000,
				)
			: rc.End || new Date();

		return {
			value: [
				EVENTS_ROW,
				rc.Start,
				blockEnd,
				rc.ScheduledRollChangeHours,
				rc.ActualRollChangeHours,
			],
			prevSectionName: rc.PrevSectionName,
			nextSectionName: rc.NextSectionName,
			scheduledHours: rc.ScheduledRollChangeHours,
			actualHours: rc.ActualRollChangeHours,
			trackedHours,
			effectiveActualHours,
			isActualEdited,
			isScheduled,
			isInProgress,
			isWarning,
			isOverdue,
			isOverSchedule,
			color,
		};
	});

	// Exclude scheduled (future) events — Gantt only shows current and past
	const activeRollChanges = rollChangesData.filter((rc) => !rc.isScheduled);
	const now = new Date();
	const activeDmhEvents = dmhEventData.filter(
		(e) => e.Start != null && new Date(e.Start) <= now,
	);

	// Merge RC blocks + D/M/H events into unified segments for the Events row
	const mergedEventSegments = mergeEventsForRow(
		activeRollChanges,
		activeDmhEvents,
		now,
	);

	// ── RC colors (lighter solid tones to avoid collision with Delays) ──
	const RC_COLORS = {
		green: "#24DA1E", // Neon green
		orange: "#FFE0B2", // Material Orange 100
		red: "#FFCDD2", // Material Red 100
	};

	// Shared renderItem factory for RC series (both modes).
	// Uses the external data array by index (same pattern as compact mode)
	// because ECharts may not propagate custom fields via params.data reliably.
	const rcHeightFactor = compactMode ? 0.3 : 0.6;
	const makeRcRenderItem = (
		dataArray: ReturnType<typeof toEventItem>[],
		color: string,
	) =>
		function (params: any, api: any) {
			const categoryIndex = api.value(0);
			const start = api.coord([api.value(1), categoryIndex]);
			const end = api.coord([api.value(2), categoryIndex]);
			const height = api.size([0, 1])[1] * rcHeightFactor;

			const minTimeCoord = api.coord([TIME_START, categoryIndex]);
			const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
			const minX = minTimeCoord[0];
			const maxX = maxTimeCoord[0];

			const finalStartX = Math.max(start[0], minX);
			const finalEndX = Math.min(end[0], maxX);
			const barWidth = Math.max(finalEndX - finalStartX, 2);

			if (start[0] > maxX || end[0] < minX) return null;

			const item = dataArray[params.dataIndex];
			if (!item) return null;

			return {
				type: "rect",
				shape: {
					x: finalStartX,
					y: start[1] - height / 2,
					width: barWidth,
					height: height,
				},
				style: {
					fill: color,
					stroke: GANTT_BAR_STROKE,
					lineWidth: 1,
					opacity: 0.8,
				},
				emphasis: {
					style: {
						fill: color,
						stroke: GANTT_BAR_STROKE,
						lineWidth: 3,
						opacity: 0.8,
					},
				},
			};
		};

	const toEventItem = (seg: EventSegment) => ({
		value: [EVENTS_ROW, seg.start, seg.end],
		segmentType: seg.type,
		color: seg.color,
		strokeColor: seg.strokeColor,
		label: seg.label,
		rcData: seg.rcData,
		eventId: seg.eventId,
		eventTypeCode: seg.eventTypeCode,
		eventTypeName: seg.eventTypeName,
		comments: seg.comments,
		createdAt: seg.createdAt,
		originalEnd: seg.originalEnd,
		isInProgress: seg.isInProgress,
		isOverdue: seg.isOverdue,
		isWarning: seg.isWarning,
		isOverSchedule: seg.isOverSchedule,
		// Propagate RC flags for Playwright test assertions
		isInProgressRc: seg.rcData?.isInProgress ?? false,
		isOverScheduleRc: seg.rcData?.isOverSchedule ?? false,
	});

	// Split RC segments into in-progress vs completed for separate series (normal mode).
	// Uses toEventItem so each item has value: [EVENTS_ROW, start, end] for renderItem.
	const rcInProgressData = mergedEventSegments
		.filter((s) => s.type === "rc" && s.isInProgress)
		.map(toEventItem);
	const rcCompletedData = mergedEventSegments
		.filter((s) => s.type === "rc" && !s.isInProgress)
		.map(toEventItem);

	// Group RC segments by their rcData identity to detect splits and add part info
	const rcSegments = mergedEventSegments.filter((s) => s.type === "rc");
	const rcSplitCounts = new Map<object, number>();
	for (const seg of rcSegments) {
		if (seg.rcData) {
			rcSplitCounts.set(seg.rcData, (rcSplitCounts.get(seg.rcData) ?? 0) + 1);
		}
	}
	const rcPartIndex = new Map<object, number>();
	const rcRowData = rcSegments.map((seg) => {
		const item = toEventItem(seg);
		if (seg.rcData) {
			const total = rcSplitCounts.get(seg.rcData) ?? 1;
			const idx = (rcPartIndex.get(seg.rcData) ?? 0) + 1;
			rcPartIndex.set(seg.rcData, idx);
			return { ...item, splitPart: idx, splitTotal: total };
		}
		return { ...item, splitPart: 1, splitTotal: 1 };
	});

	const toDmhStackItem = (
		item: ReturnType<typeof toEventItem>,
	): NonProductionStackItem => ({
		start: new Date(item.value[1] as Date).getTime(),
		end: item.originalEnd
			? new Date(item.originalEnd).getTime()
			: Number.MAX_SAFE_INTEGER,
		eventTypeCode: item.eventTypeCode,
		createdAt: item.createdAt ? new Date(item.createdAt).getTime() : null,
		eventId: item.eventId,
	});

	const dmhDrawOrderFn = (
		a: ReturnType<typeof toEventItem>,
		b: ReturnType<typeof toEventItem>,
	) => compareNonProductionStack(toDmhStackItem(b), toDmhStackItem(a));

	const allDmhItems = mergedEventSegments
		.filter((s) => s.type === "dmh")
		.map(toEventItem)
		.sort(dmhDrawOrderFn)
		.map((item, index) => ({ ...item, zOrder: index + 1 }));

	// Split DMH into per-type arrays so each gets its own series and legend entry
	const downtimeData = allDmhItems.filter((d) => d.eventTypeCode === "D");
	const maintenanceData = allDmhItems.filter((d) => d.eventTypeCode === "M");
	const holidayData = allDmhItems.filter((d) => d.eventTypeCode === "H");
	const hasEventLegendEntries =
		activeRollChanges.length > 0 ||
		downtimeData.length > 0 ||
		maintenanceData.length > 0 ||
		holidayData.length > 0;

	const carriersDataFormatted = compactMode
		? []
		: carrierData.map((d) => ({
				value: [d.standIndex, d.start, d.end || new Date(), d.carrierLabelBar],
				partId: d.partId,
				partName: d.partName,
				carrierInstanceId: d.carrierInstanceId,
				sectionName: d.sectionName,
				sectionId: d.sectionId,
				standId: d.standId,
				standName: d.standName,
				rollsetId: d.rollsetId,
				start: d.start,
				end: d.end,
				carrierLabelBar: d.carrierLabelBar,
				campaignWeight: d.campaignWeight?.toFixed(2),
				topRollId: d.topRollId,
				topRollName: d.topRollName,
				topRollAccumulatedWeight: d.topRollAccumulatedWeight?.toFixed(2),
				topRollPass: d.topRollPass,
				topRollSelectedPassAccumulatedTons:
					d.topRollSelectedPassAccumulatedTons,
				bottomRollId: d.bottomRollId,
				bottomRollName: d.bottomRollName,
				bottomRollAccumulatedWeight: d.bottomRollAccumulatedWeight?.toFixed(2),
				bottomRollPass: d.bottomRollPass,
				bottomRollSelectedPassAccumulatedTons:
					d.bottomRollSelectedPassAccumulatedTons,
				operatorName: d.operatorName,
				carrierStatus: d.carrierStatus,
				bait: d.bait,
			}));

	const tphValues = productionData
		.filter((d) => d.EventTypeCode === "B" && d.TPH)
		.map((d) => d.TPH);
	const maxTPH = Math.max(...tphValues);
	const minTPH = Math.min(...tphValues);

	// Median TPH: fallback for short-duration blocks where TPH is unreliable
	const sortedTPH = [...tphValues].sort((a, b) => a - b);
	const medianTPH =
		sortedTPH.length > 0
			? sortedTPH.length % 2 === 0
				? (sortedTPH[sortedTPH.length / 2 - 1] +
						sortedTPH[sortedTPH.length / 2]) /
					2
				: sortedTPH[Math.floor(sortedTPH.length / 2)]
			: 0;

	// Minimum duration (seconds) for TPH to be statistically reliable
	const MIN_RELIABLE_DURATION_SEC = 300;

	const getDayLabelsInfo = () => {
		if (!start || !end) return { startDay: null, midnight: null };

		const startMoment = moment(start);
		const endMoment = moment(end);

		const startDay = {
			label: startMoment.format("ddd, MMM DD"),
		};

		const midnight = startMoment.clone().startOf("day").add(1, "day");
		let midnightInfo = null;
		if (midnight.isAfter(startMoment) && midnight.isBefore(endMoment)) {
			const totalDuration = endMoment.diff(startMoment);
			const midnightOffset = midnight.diff(startMoment);
			const percentage = (midnightOffset / totalDuration) * 100;
			midnightInfo = {
				timestamp: midnight.toDate(),
				percentage,
				label: midnight.format("ddd, MMM DD"),
			};
		}

		return { startDay, midnight: midnightInfo };
	};

	const dayLabelsInfo = getDayLabelsInfo();

	const formatComments = (comments: string) => {
		const words = comments.split(" ");
		const commentLines: string[] = [];
		let currentLine = "";

		for (const word of words) {
			if ((currentLine + " " + word).trim().length > 100) {
				commentLines.push(currentLine.trim());
				currentLine = word;
			} else {
				currentLine = (currentLine + " " + word).trim();
			}
		}
		if (currentLine) commentLines.push(currentLine);

		return commentLines.join("<br/>");
	};

	// Main chart configuration
	const option = {
		toolbox: {
			show: true,
			right: 10,
			top: compactMode ? 0 : 0,
			feature: {
				saveAsImage: {
					type: "png",
					pixelRatio: 2,
					title: "Download",
					name: "gantt-chart",
				},
			},
		},
		tooltip: {
			trigger: "item",
			formatter: function (params: any) {
				const start =
					dxtToLocalServerTime(params.data.value[1], "MM/dd HH:mm:ss") || "";
				const end =
					dxtToLocalServerTime(params.data.value[2], "MM/dd HH:mm:ss") || "";

				const startDate = new Date(params.data.value[1]);
				const endDate = new Date(params.data.value[2]);
				const diff = moment(endDate).diff(moment(startDate));
				const duration = moment.duration(diff).format("h[h] m[m] s[s]");

				if (params.seriesName === "Billets") {
					const durationDisplay = diff > 0 ? duration : "-";
					const pushRateDisplay =
						diff > 0 && params.data.pushRate
							? moment
									.duration(params.data.pushRate, "seconds")
									.format("h[h] m[m] s[s]")
							: "-";
					return `<strong>Billet Production</strong><br/>
					   Heat: ${params.data.heatName}<br/>
                       Time: ${start} - ${end}<br/>
                       Duration: ${durationDisplay}<br/>
                       Billets: ${params.data.billets}<br/>
                       Weight: ${parseFloat(params.data.weight?.toFixed(1)) || 0} tns<br/>
                       Push Rate: ${pushRateDisplay}<br/>
                       TPH: ${params.data.tph ? parseFloat(params.data.tph.toFixed(2)) : "-"}`;
				} else if (params.seriesName === "Sections") {
					const activeTime = moment
						.duration(params.data.activeTimeCurrentShiftSecs, "seconds")
						.format("h[h] m[m] s[s]");
					const spansMultipleShifts =
						params.data.sectionSpansMultipleShifts === true;
					let tooltip = `<strong>Section: ${params.data.sectionName}</strong><br/>
                       Active time for this shift: ${activeTime}`;
					if (spansMultipleShifts) {
						const rollTime = moment
							.duration(params.data.rollTimeSecs, "seconds")
							.format("h[h] m[m] s[s]");
						const rollingStart =
							dxtToLocalServerTime(params.data.rollingStart, "MM/dd HH:mm") ??
							"";
						tooltip += `<br/>Roll Time: ${rollTime}<br/>Rolling Start: ${rollingStart}`;
					}
					return tooltip;
				} else if (params.seriesName === "Carriers") {
					const status = assemblyStatusCodes.find(
						(c) => c.Code == params.data.carrierStatus,
					);
					const statusChip = ` <span style="display:inline-block;padding:1px 8px;border-radius:9999px;font-size:0.75em;
							font-weight:600;background:${status?.Color};color:${status?.TextColor};vertical-align:middle;margin-left:6px;
						">
							${status?.Name}
						</span>`;

					const header = `<div style="display:flex; justify-content:space-between; align-items:center;">
										<strong>Carrier ${params.data.partName}</strong>
										${statusChip}
									</div>`;

					return `${header}
						Section: ${params.data.sectionName}<br/>
						Stand: ${params.data.standName}<br/>
						Campaign: ${params.data.campaignWeight} tn<br/>
						Bait: ${params.data.bait ?? ""}<br/>
						Operator: ${params.data.operatorName ?? ""}<br/>
						<br/>
						<strong>Top Roll: ${params.data.topRollName}</strong><br/>
						Accumulated: ${params.data.topRollAccumulatedWeight} tn<br/>
						${
							params.data.topRollPass
								? `Current Pass: ${params.data.topRollPass}<br/>
							  Accum. Pass Tons: ${params.data.topRollSelectedPassAccumulatedTons} tn<br/>`
								: ""
						}
						<br/>
						<strong>Bottom Roll: ${params.data.bottomRollName}</strong><br/>
						Accumulated: ${params.data.bottomRollAccumulatedWeight} tn<br/>
						${
							params.data.bottomRollPass
								? `Current Pass: ${params.data.bottomRollPass}<br/>
							  Accum. Pass Tons: ${params.data.bottomRollSelectedPassAccumulatedTons} tn<br/>`
								: ""
						}
						<br/>
						Installed At: ${start}<br/>
						Uninstalled At: ${params.data.end ? end : "-"}<br/>`;
				} else if (params.seriesName === "Cobbles") {
					return `<strong>Cobble Event</strong><br/>
                       Stand: ${params.data.lastStand}<br/>
                       Time: ${start} - ${end}<br/>
                       Duration: ${duration}<br/>
                       Cobbles: ${params.data.cobbles}`;
				} else if (isDelayData(params.data)) {
					const timeEnd = params.data.end
						? end
						: `(${dxtToLocalServerTime(new Date(), "HH:mm:ss")})`;
					return `<strong>Delay: ${params.data.delayName}</strong><br/>
                       Category: ${params.data.category}<br/>
                       Time: ${start} - ${timeEnd}<br/>
                       Duration: ${duration}<br/>
                       Comments: ${formatComments(params.data.comments ?? "-")}`;
				} else if (params.seriesName === "Adjustments") {
					return `<strong>Adjustment: ${params.data.adjustmentType}</strong><br/>
						Reason: ${params.data.adjustmentReason}<br/>
						Value: ${params.data.adjustmentValue}<br/>
						Stand: ${params.data.standName}<br/>
						Operator: ${params.data.operatorName}<br/>
						Time: ${start}`;
				} else if (params.seriesName === "Pass Changes") {
					const prevPass = params.data.prevPassNumber
						? `Pass ${params.data.prevPassNumber}`
						: "-";
					const newPass = `Pass ${params.data.passNumber}`;
					return `<strong>Pass Change</strong><br/>
						Carrier: ${params.data.carrierName}<br/>
						Previous: ${prevPass}<br/>
						New: ${newPass}<br/>
						Stand: ${params.data.standName}<br/>
						Section: ${params.data.sectionName}<br/>
						Time: ${start}`;
				} else if (
					["Downtime", "Maintenance", "Holiday"].includes(params.seriesName)
				) {
					const renderDmhBlock = (item: (typeof allDmhItems)[number]) => {
						const itemStart =
							dxtToLocalServerTime(item.value[1] as Date, "MM/dd HH:mm:ss") ||
							"";
						const itemEnd =
							dxtToLocalServerTime(item.value[2] as Date, "MM/dd HH:mm:ss") ||
							"";
						const itemDuration = moment
							.duration(
								moment(item.value[2] as Date).diff(
									moment(item.value[1] as Date),
								),
							)
							.format("h[h] m[m] s[s]");
						const itemName =
							item.eventTypeName ??
							DMH_COLORS[item.eventTypeCode ?? ""]?.name ??
							params.seriesName;
						return `<strong>${itemName}</strong><br/>
                       Time: ${itemStart} - ${itemEnd}<br/>
                       Duration: ${itemDuration}<br/>
                       Comments: ${item.comments || "-"}`;
					};

					const hoveredStart = new Date(params.data.value[1]).getTime();
					const hoveredEnd = new Date(params.data.value[2]).getTime();
					const overlapping = allDmhItems
						.filter(
							(item) =>
								item.eventId !== params.data.eventId &&
								new Date(item.value[1] as Date).getTime() < hoveredEnd &&
								new Date(item.value[2] as Date).getTime() > hoveredStart,
						)
						.sort((a, b) => dmhDrawOrderFn(b, a));

					if (overlapping.length === 0) return renderDmhBlock(params.data);

					const separator = `<div style="border-top:1px solid rgba(0,0,0,0.12);margin:6px 0;"></div>`;
					const overlappingTitle = `<span style="opacity:0.7;">Also in this period (${overlapping.length})</span><br/>`;

					return [
						renderDmhBlock(params.data),
						overlappingTitle + overlapping.map(renderDmhBlock).join(separator),
					].join(separator);
				} else if (
					params.seriesName === "Roll Change" ||
					params.seriesName === "RC Overdue" ||
					params.seriesName === "RC Warning" ||
					params.seriesName === "RC In Progress"
				) {
					const rc = params.data.rcData;
					if (!rc) return "";
					const scheduled = rc.scheduledHours;
					const actual = rc.actualHours;
					const tracked = rc.trackedHours ?? actual;
					const effectiveActual = rc.effectiveActualHours ?? actual;
					const fmt = (h: number) => {
						const totalMinutes = Math.round(h * 60);
						const hours = Math.floor(totalMinutes / 60);
						const minutes = totalMinutes % 60;

						if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
						if (hours > 0) return `${hours}h`;
						return `${minutes}m`;
					};
					// Split info
					const splitPart = params.data.splitPart ?? 1;
					const splitTotal = params.data.splitTotal ?? 1;
					const isSplit = splitTotal > 1;
					const splitLabel = isSplit
						? ` (Part ${splitPart} of ${splitTotal})`
						: "";

					// Status chip (top-right)
					let chipHtml = "";
					if (rc.isInProgress && rc.isOverdue) {
						chipHtml = ` <span style="display:inline-block;padding:1px 8px;border-radius:9999px;font-size:0.75em;font-weight:600;background:#FFEBEE;color:#B71C1C;vertical-align:middle;margin-left:6px;">Overdue</span>`;
					} else if (rc.isInProgress) {
						chipHtml = ` <span style="display:inline-block;padding:1px 8px;border-radius:9999px;font-size:0.75em;font-weight:600;background:#E8F5E9;color:#1B5E20;vertical-align:middle;margin-left:6px;">In Progress</span>`;
					} else if (rc.isOverSchedule) {
						chipHtml = ` <span style="display:inline-block;padding:1px 8px;border-radius:9999px;font-size:0.75em;font-weight:600;background:#FFEBEE;color:#B71C1C;vertical-align:middle;margin-left:6px;">Overdue</span>`;
					}

					const header = `<div style="display:flex;justify-content:space-between;align-items:center;"><strong>Roll Change${splitLabel}</strong>${chipHtml}</div>`;

					const trackedStr = tracked != null ? fmt(tracked) : "-";
					const actualStr =
						effectiveActual != null ? fmt(effectiveActual) : "-";
					const scheduledStr = scheduled ? fmt(scheduled) : "-";
					const editedActualHtml = rc.isActualEdited
						? ` <span style="display:inline-block;padding:1px 6px;border-radius:9999px;font-size:0.75em;font-weight:600;background:#FFF3E0;color:#E65100;vertical-align:middle;margin-left:6px;">Edited</span>`
						: "";
					let diffText = "-";
					if (scheduled && effectiveActual != null) {
						const deltaMinutes = Math.round((effectiveActual - scheduled) * 60);
						if (deltaMinutes > 0) diffText = `${fmt(deltaMinutes / 60)} over`;
						else if (deltaMinutes < 0)
							diffText = `${fmt(Math.abs(deltaMinutes) / 60)} early`;
						else diffText = "on time";
					}

					return `${header}
                       ${rc.prevSectionName ?? "-"} → ${rc.nextSectionName ?? "-"}<br/>
                       Time: ${start} - ${end}<br/>
                       Tracked: ${trackedStr}<br/>
                       Actual: ${actualStr}${editedActualHtml}<br/>
                       Scheduled: ${scheduledStr}<br/>
                       Diff: ${diffText}`;
				} else if (params.seriesName === "Gap Time") {
					return `<strong>Gap Time</strong><br/>
                       Time: ${start} - ${end}<br/>
                       Duration: ${duration}`;
				}
			},
		},
		legend: {
			data: [
				// ── Legends shown only in normal mode ──
				...(!compactMode
					? [
							{
								name: "Carriers",
								icon: "rect" as const,
								itemStyle: { color: "#FF9800" },
							},
							{
								name: "Cobbles",
								icon: "rect" as const,
								itemStyle: { color: "#F44336" },
							},
						]
					: []),

				// ── Event legend group ──
				...(hasEventLegendEntries
					? [
							{
								name: EVENT_LEGEND_LABEL_SERIES,
								icon: "none" as const,
							},
						]
					: []),

				// ── RC legends — unified logic for both modes ──
				...(activeRollChanges.length > 0
					? [
							...(activeRollChanges.some((d) => d.isInProgress)
								? [
										...(activeRollChanges.some((d) => d.isOverdue)
											? [
													{
														name: "RC Overdue",
														icon: "rect" as const,
														itemStyle: { color: RC_COLORS.red },
													},
												]
											: []),
										...(activeRollChanges.some(
											(d) => d.isWarning && !d.isOverdue,
										)
											? [
													{
														name: "RC Warning",
														icon: "rect" as const,
														itemStyle: { color: RC_COLORS.orange },
													},
												]
											: []),
										...(activeRollChanges.some(
											(d) => d.isInProgress && !d.isOverdue && !d.isWarning,
										)
											? [
													{
														name: "RC In Progress",
														icon: "rect" as const,
														itemStyle: { color: RC_COLORS.green },
													},
												]
											: []),
									]
								: []),
							...(activeRollChanges.every((d) => !d.isInProgress)
								? [
										...(activeRollChanges.some((d) => d.isOverSchedule)
											? [
													{
														name: "RC Overdue",
														icon: "rect" as const,
														itemStyle: { color: RC_COLORS.red },
													},
												]
											: []),
										...(activeRollChanges.every((d) => !d.isOverSchedule)
											? [
													{
														name: "Roll Change",
														icon: "rect" as const,
														itemStyle: { color: RC_COLORS.green },
													},
												]
											: []),
									]
								: []),
						]
					: []),

				// ── D/M/H legends — both modes, only if data exists ──
				...(downtimeData.length > 0
					? [
							{
								name: DMH_COLORS.D.name,
								icon: "rect" as const,
								itemStyle: { color: DMH_COLORS.D.color },
							},
						]
					: []),
				...(maintenanceData.length > 0
					? [
							{
								name: DMH_COLORS.M.name,
								icon: "rect" as const,
								itemStyle: { color: DMH_COLORS.M.color },
							},
						]
					: []),
				...(holidayData.length > 0
					? [
							{
								name: DMH_COLORS.H.name,
								icon: "rect" as const,
								itemStyle: { color: DMH_COLORS.H.color },
							},
						]
					: []),

				// ── Legends shown only in normal mode ──
				...(!compactMode
					? [
							{
								name: "Adjustments",
								icon: "rect" as const,
								itemStyle: { color: "#9C27B0" },
							},
							{
								name: "Pass Changes",
								icon: "circle" as const,
								itemStyle: { color: "#00BCD4" },
							},
						]
					: []),

				// ── Delay legend group — always last ──
				...(delayCategories.length > 0
					? [
							...(hasEventLegendEntries
								? [
										// Adds one item width between sections without changing legend wrapping.
										{
											name: LEGEND_SECTION_SPACER_SERIES,
											icon: "rect" as const,
											itemStyle: { color: "transparent" },
											inactiveColor: "transparent",
											inactiveBorderColor: "transparent",
										},
									]
								: []),
							{
								name: DELAY_LEGEND_LABEL_SERIES,
								icon: "none" as const,
							},
							...delayCategories.map((category) => ({
								name: category.seriesName,
								icon: "rect" as const,
								itemStyle: { color: category.color },
							})),
						]
					: []),
			],
			formatter: (name: string) => {
				if (name === EVENT_LEGEND_LABEL_SERIES) return "Events:";
				if (name === LEGEND_SECTION_SPACER_SERIES) return "";
				if (name === DELAY_LEGEND_LABEL_SERIES) return "Delays:";
				return delayLegendLabels.get(name) ?? name;
			},
			show: true,
			bottom: compactMode ? 0 : 20,
			right: undefined,
			itemGap: LEGEND_ITEM_GAP,
			itemWidth: 12,
			itemHeight: 10,
			textStyle: { fontSize: compactMode ? 10 : 12 },
			type: "plain",
		},
		grid: {
			top: compactMode ? 20 : 10,
			left: 80,
			right: 50,
			bottom: compactMode ? 80 : 80,
			containLabel: false,
			clip: true,
		},
		xAxis: {
			type: "time",
			min: TIME_START, // Use global time range
			max: TIME_END, // Use global time range
			axisLabel: {
				formatter: function (value: any) {
					return dxtToLocalServerTime(value, "HH:mm") || "";
				},
			},
			splitLine: {
				show: true,
				lineStyle: {
					color: "#f0f0f0",
				},
			},
		},
		yAxis: {
			type: "category",
			data: stands,
			inverse: true,
			axisLabel: {
				fontSize: 12,
				color: "#333",
			},
			axisTick: { show: false },
			axisLine: { show: false },
			splitLine: {
				show: true,
				lineStyle: {
					color: "#f0f0f0",
				},
			},
			interval: 0,
			boundaryGap: true,
			axisPointer: {
				type: "shadow",
			},
			splitArea: {
				show: true,
				areaStyle: {
					color: stands.map((stand, index) => {
						if (
							!compactMode &&
							hoveredRowIndex === index &&
							index > DELAYS_ROW
						) {
							return "rgba(33, 150, 243, 0.1)";
						}
						if (compactMode) {
							return index <= 2 ? "rgba(0,0,0,0.02)" : "transparent";
						}
						if (index >= 0 && index <= 4) {
							return "rgba(0,0,0,0.02)";
						}
						return "transparent";
					}),
				},
			},
		},
		series: [
			...(hasEventLegendEntries
				? [
						{
							name: EVENT_LEGEND_LABEL_SERIES,
							type: "scatter",
							data: [],
							silent: true,
							legendHoverLink: false,
						},
					]
				: []),
			...(hasEventLegendEntries && delayCategories.length > 0
				? [
						{
							name: LEGEND_SECTION_SPACER_SERIES,
							type: "scatter",
							data: [],
							silent: true,
							legendHoverLink: false,
						},
					]
				: []),
			// Series 1: Billet Production (Blue bars)
			{
				name: "Billets",
				type: "custom",
				z: 1,
				clip: true,
				renderItem: function (params: any, api: any) {
					const categoryIndex = api.value(0);
					const start = api.coord([api.value(1), categoryIndex]);
					const end = api.coord([api.value(2), categoryIndex]);

					const tph = api.value(5) || 0;
					const startTime = new Date(api.value(1)).getTime();
					const endTime = new Date(api.value(2)).getTime();
					const durationSec = (endTime - startTime) / 1000;

					// Use real TPH for blocks with enough duration; fallback to median
					// for short blocks where TPH is inflated (few billets / tiny timespan)
					const effectiveTPH =
						durationSec >= MIN_RELIABLE_DURATION_SEC ? tph : medianTPH;
					const normalizedTPH =
						maxTPH > minTPH
							? Math.max(
									Math.min((effectiveTPH - minTPH) / (maxTPH - minTPH), 1),
									0,
								)
							: 0.5;
					const heightFactor = compactMode
						? 1.5 + normalizedTPH * 2
						: 2.0 + normalizedTPH * 3.0;
					const rowHeight = api.size([0, 1])[1];
					const height = Math.max(rowHeight * heightFactor, rowHeight * 0.8);

					// Get X-axis boundary coordinates to clip bars
					const minTimeCoord = api.coord([TIME_START, categoryIndex]);
					const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
					const minX = minTimeCoord[0];
					const maxX = maxTimeCoord[0];

					// Clip bars at both ends
					const finalStartX = Math.max(start[0], minX);
					const finalEndX = Math.min(end[0], maxX);
					const barWidth = Math.max(finalEndX - finalStartX, 3);

					// Don't render bars completely outside the range
					if (start[0] > maxX || end[0] < minX) return null;

					const showText = barWidth > 29;

					const heightPerText = 15;
					const fontSize = 10;
					const isHovered = hoveredHeatId === api.value(7);

					const colors = getColors(api.value(7));
					const textStyle = {
						textFill: colors.text,
						textStroke: colors.fill,
						textStrokeWidth: 1,
						fontSize: fontSize,
						fontWeight: "bold",
						textVerticalAlign: "middle",
						textOverflow: "truncate",
					};

					return {
						type: "group",
						children: [
							{
								type: "rect",
								shape: {
									x: finalStartX,
									y: start[1] + rowHeight / 2 - height,
									width: barWidth,
									height: height,
								},
								style: {
									fill: getColors(api.value(7)).fill,
									stroke: isHovered
										? getColors(api.value(7)).highlighted
										: getColors(api.value(7)).border,
									lineWidth: isHovered ? 3 : 1,
								},
							},
							// Line 1: Heat Name
							{
								type: "text",
								ignore: !showText,
								clipPath: {
									type: "rect",
									shape: {
										x: finalStartX,
										y: start[1] + rowHeight / 2 - height,
										width: barWidth,
										height: height,
									},
								},
								style: {
									x: finalStartX + 3,
									y: start[1] + rowHeight / 2 - height + heightPerText,
									text: `${api.value(8)}`,
									...textStyle,
								},
							},
							// Line 2: Section Name
							{
								type: "text",
								ignore: !showText,
								clipPath: {
									type: "rect",
									shape: {
										x: finalStartX,
										y: start[1] + rowHeight / 2 - height,
										width: barWidth,
										height: height,
									},
								},
								style: {
									x: finalStartX + 3,
									y: start[1] + rowHeight / 2 - height + heightPerText * 2,
									text: `${api.value(6)}`,
									...textStyle,
								},
							},
							// Line 3: Billets
							{
								type: "text",
								ignore: !showText,
								clipPath: {
									type: "rect",
									shape: {
										x: finalStartX,
										y: start[1] + rowHeight / 2 - height,
										width: barWidth,
										height: height,
									},
								},
								style: {
									x: finalStartX + 3,
									y: start[1] + rowHeight / 2 - height + heightPerText * 3,
									text: `${api.value(3)} bt`,
									...textStyle,
								},
							},
							// Line 4: Weight
							{
								type: "text",
								ignore: !showText,
								clipPath: {
									type: "rect",
									shape: {
										x: finalStartX,
										y: start[1] + rowHeight / 2 - height,
										width: barWidth,
										height: height,
									},
								},
								style: {
									x: finalStartX + 3,
									y: start[1] + rowHeight / 2 - height + heightPerText * 4,
									text: `${api.value(4)?.toFixed(1)}t`,
									...textStyle,
								},
							},
							// Line 5: TPH
							{
								type: "text",
								ignore: !showText,
								clipPath: {
									type: "rect",
									shape: {
										x: finalStartX,
										y: start[1] + rowHeight / 2 - height,
										width: barWidth,
										height: height,
									},
								},
								style: {
									x: finalStartX + 3,
									y: start[1] + rowHeight / 2 - height + heightPerText * 5,
									text: `${parseFloat(tph?.toFixed(2) ?? "")} tph`,
									...textStyle,
								},
							},
							// Line 6: Duration
							{
								type: "text",
								ignore: !showText,
								clipPath: {
									type: "rect",
									shape: {
										x: finalStartX,
										y: start[1] + rowHeight / 2 - height,
										width: barWidth,
										height: height,
									},
								},
								style: {
									x: finalStartX + 3,
									y: start[1] + rowHeight / 2 - height + heightPerText * 6,
									text: `${(() => {
										const startDate = new Date(api.value(1));
										const endDate = new Date(api.value(2));
										const diff = moment(endDate).diff(moment(startDate));
										const duration = moment
											.duration(diff)
											.format("h[hs] m[m] s[s]");

										return duration;
									})()}`,
									...textStyle,
								},
							},
						],
					};
				},
				encode: {
					x: [1, 2],
					y: 0,
					tooltip: [0, 1, 2, 3, 4, 5],
				},
				data: billetsData,
			},
			// Series 2: Section info
			{
				name: "Sections",
				type: "custom",
				z: 1,
				renderItem: function (params: any, api: any) {
					const categoryIndex = api.value(0);
					const start = api.coord([api.value(1), categoryIndex]);
					const end = api.coord([api.value(2), categoryIndex]);

					// Get X-axis boundary coordinates to clip bars
					const minTimeCoord = api.coord([TIME_START, categoryIndex]);
					const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
					const minX = minTimeCoord[0];
					const maxX = maxTimeCoord[0];

					// Clip bars at both ends
					const finalStartX = Math.max(start[0], minX);
					const finalEndX = Math.min(end[0], maxX);
					const barWidth = Math.max(finalEndX - finalStartX, 0);

					// Don't render bars completely outside the range
					if (start[0] > maxX || end[0] < minX) return null;

					const rowHeight = api.size([0, 1])[1];
					const isSectionHovered = hoveredSectionId === api.value(3);

					const colors = getColors(api.value(3));

					return {
						type: "group",
						children: [
							{
								type: "rect",
								shape: {
									x: finalStartX,
									y: start[1] + rowHeight / 2,
									width: barWidth,
									height: 15,
								},
								style: {
									fill: colors.fill,
									stroke: isSectionHovered ? colors.highlighted : colors.fill,
									lineWidth: isSectionHovered ? 3 : 1,
								},
							},
							// Section Name
							{
								type: "text",
								clipPath: {
									type: "rect",
									shape: {
										x: finalStartX,
										y: start[1] + rowHeight / 2,
										width: barWidth,
										height: 15,
									},
								},
								style: {
									x: finalStartX + barWidth / 2,
									y: start[1] + rowHeight / 2 + 7.5,
									text: `${api.value(4)}`,
									textFill: colors.text,
									textStroke: colors.fill,
									textStrokeWidth: 1,
									fontSize: 10,
									fontWeight: "bold",
									textAlign: "center",
									textVerticalAlign: "middle",
								},
							},
						],
					};
				},
				encode: {
					x: [1, 2],
					y: 0,
					tooltip: [0, 1, 2, 3, 4, 5],
				},
				data: sectionsRowData,
			},

			// Series 3: Roll Change — separate series per state (both modes)
			...(() => {
				const rcOverdueInProgress = rcInProgressData.filter((d) => d.isOverdue);
				const rcWarningInProgress = rcInProgressData.filter(
					(d) => !d.isOverdue && d.isWarning,
				);
				const rcGreenInProgress = rcInProgressData.filter(
					(d) => !d.isOverdue && !d.isWarning,
				);
				const rcOverdueCompleted = rcCompletedData.filter(
					(d) => d.isOverSchedule,
				);
				const rcGreenCompleted = rcCompletedData.filter(
					(d) => !d.isOverSchedule,
				);

				return [
					...(rcOverdueInProgress.length > 0
						? [
								{
									name: "RC Overdue" as const,
									type: "custom" as const,
									z: 2,
									tooltip: { borderColor: RC_COLORS.red },
									renderItem: makeRcRenderItem(
										rcOverdueInProgress,
										RC_COLORS.red,
									),
									encode: { x: [1, 2], y: 0, tooltip: [0, 1, 2] },
									data: rcOverdueInProgress,
								},
							]
						: []),
					...(rcWarningInProgress.length > 0
						? [
								{
									name: "RC Warning" as const,
									type: "custom" as const,
									z: 2,
									tooltip: { borderColor: RC_COLORS.orange },
									renderItem: makeRcRenderItem(
										rcWarningInProgress,
										RC_COLORS.orange,
									),
									encode: { x: [1, 2], y: 0, tooltip: [0, 1, 2] },
									data: rcWarningInProgress,
								},
							]
						: []),
					...(rcGreenInProgress.length > 0
						? [
								{
									name: "RC In Progress" as const,
									type: "custom" as const,
									z: 2,
									tooltip: { borderColor: RC_COLORS.green },
									renderItem: makeRcRenderItem(
										rcGreenInProgress,
										RC_COLORS.green,
									),
									encode: { x: [1, 2], y: 0, tooltip: [0, 1, 2] },
									data: rcGreenInProgress,
								},
							]
						: []),
					...(rcOverdueCompleted.length > 0
						? [
								{
									name: "RC Overdue" as const,
									type: "custom" as const,
									z: 2,
									tooltip: { borderColor: RC_COLORS.red },
									renderItem: makeRcRenderItem(
										rcOverdueCompleted,
										RC_COLORS.red,
									),
									encode: { x: [1, 2], y: 0, tooltip: [0, 1, 2] },
									data: rcOverdueCompleted,
								},
							]
						: []),
					...(rcGreenCompleted.length > 0
						? [
								{
									name: "Roll Change" as const,
									type: "custom" as const,
									z: 2,
									tooltip: { borderColor: RC_COLORS.green },
									renderItem: makeRcRenderItem(
										rcGreenCompleted,
										RC_COLORS.green,
									),
									encode: { x: [1, 2], y: 0, tooltip: [0, 1, 2] },
									data: rcGreenCompleted,
								},
							]
						: []),
				];
			})(),

			// Series 4: D/M/H — one series per event type so legend names match
			...NON_PRODUCTION_EVENT_TYPE_CODES.flatMap((code) => {
				const dmhInfo = DMH_COLORS[code];
				if (!dmhInfo) return [];
				const dataArray =
					code === "D"
						? downtimeData
						: code === "M"
							? maintenanceData
							: holidayData;
				if (dataArray.length === 0) return [];
				return [
					{
						name: dmhInfo.name,
						type: "custom" as const,
						z: 2,
						tooltip: { borderColor: dmhInfo.color },
						renderItem: function (params: any, api: any) {
							const categoryIndex = api.value(0);
							const start = api.coord([api.value(1), categoryIndex]);
							const end = api.coord([api.value(2), categoryIndex]);
							const height = api.size([0, 1])[1] * (compactMode ? 0.3 : 0.6);

							const minTimeCoord = api.coord([TIME_START, categoryIndex]);
							const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
							const minX = minTimeCoord[0];
							const maxX = maxTimeCoord[0];

							const finalStartX = Math.max(start[0], minX);
							const finalEndX = Math.min(end[0], maxX);
							const barWidth = Math.max(finalEndX - finalStartX, 2);

							if (start[0] > maxX || end[0] < minX) return null;

							const item = dataArray[params.dataIndex];
							if (!item) return null;

							const textPaddingLeft = 4;
							const hasComments = !!item.comments;
							const showText = !compactMode && hasComments && barWidth > 50;

							return {
								type: "group",
								children: [
									{
										type: "rect",
										z2: item.zOrder,
										shape: {
											x: finalStartX,
											y: start[1] - height / 2,
											width: barWidth,
											height: height,
										},
										style: {
											fill: dmhInfo.color,
											stroke: GANTT_BAR_STROKE,
											lineWidth: 1,
										},
										emphasis: {
											style: {
												fill: dmhInfo.color,
												stroke: GANTT_BAR_STROKE,
												lineWidth: 3,
											},
										},
									},
									{
										type: "text",
										ignore: !showText,
										z2: item.zOrder,
										style: {
											x: finalStartX + textPaddingLeft,
											y: start[1],
											width: barWidth - textPaddingLeft * 2,
											text: item.comments,
											textFill: dmhInfo.text,
											fontSize: 10,
											fontWeight: "bold",
											textVerticalAlign: "middle",
											overflow: "truncate",
											ellipsis: "...",
										},
									},
								],
							};
						},
						encode: { x: [1, 2], y: 0, tooltip: [0, 1, 2] },
						data: dataArray,
					},
				];
			}),

			// Series 4: Carrier Installations (Orange bars)
			{
				name: "Carriers",
				type: "custom",
				z: 2,
				renderItem: function (params: any, api: any) {
					const categoryIndex = api.value(0);
					const start = api.coord([api.value(1), categoryIndex]);
					const end = api.coord([api.value(2), categoryIndex]);
					const height = api.size([0, 1])[1] * 0.4;

					// Get X-axis boundary coordinates to clip bars
					const minTimeCoord = api.coord([TIME_START, categoryIndex]);
					const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
					const minX = minTimeCoord[0];
					const maxX = maxTimeCoord[0];

					// Clip bars at both ends
					const finalStartX = Math.max(start[0], minX);
					const finalEndX = Math.min(end[0], maxX);
					const barWidth = Math.max(finalEndX - finalStartX, 0);

					// Don't render bars completely outside the range
					if (start[0] > maxX || end[0] < minX) return null;

					const textPaddingLeft = 8;
					const showText = barWidth > 50;
					const isHighlighted =
						highlightedCarrierInstanceId != null &&
						carriersDataFormatted[params.dataIndex]?.carrierInstanceId ===
							highlightedCarrierInstanceId;

					return {
						type: "group",
						children: [
							{
								type: "rect",
								shape: {
									x: finalStartX,
									y: start[1] - height / 2,
									width: barWidth,
									height: height,
								},
								style: {
									fill: isHighlighted ? "#d69432ff" : "#FF9800",
									stroke: "#F57C00",
									lineWidth: isHighlighted ? 2 : 1,
									opacity: isHighlighted ? 1 : 0.7,
								},
							},
							{
								type: "text",
								ignore: !showText,
								style: {
									x: finalStartX + textPaddingLeft,
									y: start[1],
									text: api.value(3),
									textFill: "#854c01",
									fontSize: 10,
									fontWeight: "bold",
									textVerticalAlign: "middle",
									textOverflow: "truncate",
								},
							},
						],
					};
				},
				encode: {
					x: [1, 2],
					y: 0,
					tooltip: [0, 1, 2],
				},
				data: carriersDataFormatted,
			},

			// Series 4: Cobble Events (Red rectangles)
			{
				name: "Cobbles",
				type: "custom",
				z: 4,
				renderItem: function (params: any, api: any) {
					const categoryIndex = api.value(0);
					const start = api.coord([api.value(1), categoryIndex]);
					const end = api.coord([api.value(2), categoryIndex]);
					const height = api.size([0, 1])[1] * 0.15;

					// Get X-axis boundary coordinates to clip bars
					const minTimeCoord = api.coord([TIME_START, categoryIndex]);
					const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
					const minX = minTimeCoord[0];
					const maxX = maxTimeCoord[0];

					// Clip bars at both ends (minimum width of 6 pixels)
					const finalStartX = Math.max(start[0], minX);
					const finalEndX = Math.min(end[0], maxX);
					const barWidth = Math.max(finalEndX - finalStartX, 6);

					// Don't render bars completely outside the range
					if (start[0] > maxX || end[0] < minX) return null;

					return {
						type: "rect",
						row: {
							height: 1000,
						},
						shape: {
							x: finalStartX,
							y: start[1] - height / 2 + 10,
							width: barWidth,
							height: height,
						},
						style: {
							fill: "#F44336",
							stroke: "#D32F2F",
							lineWidth: 2,
						},
					};
				},
				encode: {
					x: [1, 2],
					y: 0,
					tooltip: [0, 1, 2],
				},
				data: cobblesData,
			},

			// Series 5: Delay Events — internal names isolate dynamic categories
			...(delayCategories.length > 0
				? [
						{
							name: DELAY_LEGEND_LABEL_SERIES,
							type: "scatter",
							data: [],
							silent: true,
							legendHoverLink: false,
						},
					]
				: []),
			...delayCategories.map((category) => ({
				name: category.seriesName,
				type: "custom",
				z: 3,
				renderItem: function (params: any, api: any) {
					const categoryIndex = api.value(0);
					const start = api.coord([api.value(1), categoryIndex]);
					const end = api.coord([api.value(2), categoryIndex]);
					const height = api.size([0, 1])[1] * (compactMode ? 0.3 : 0.6);

					// Get X-axis boundary coordinates to clip bars
					const minTimeCoord = api.coord([TIME_START, categoryIndex]);
					const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
					const minX = minTimeCoord[0];
					const maxX = maxTimeCoord[0];

					// Clip bars at both ends (minimum width of 2 pixels)
					const finalStartX = Math.max(start[0], minX);
					const finalEndX = Math.min(end[0], maxX);
					const barWidth = Math.max(finalEndX - finalStartX, 2);

					// Don't render bars completely outside the range
					if (start[0] > maxX || end[0] < minX) return null;

					const isDelayHovered =
						hoveredDelayId === category.data[params.dataIndex]?.delayId;

					return {
						type: "rect",
						shape: {
							x: finalStartX,
							y: start[1] - height / 2,
							width: barWidth,
							height: height,
						},
						style: {
							fill:
								category.data[params.dataIndex]?.color ?? DELAY_FALLBACK_COLOR,
							stroke: GANTT_BAR_STROKE,
							lineWidth: isDelayHovered ? 3 : 1,
							opacity: 0.8,
						},
					};
				},
				encode: {
					x: [1, 2],
					y: 0,
					tooltip: [0, 1, 2],
				},
				data: category.data,
			})),

			// Series 6: Gap Time (grey bars, only in compact mode for M1)
			{
				name: "Gap Time",
				type: "custom",
				z: 2,
				renderItem: function (params: any, api: any) {
					if (!hasGapTime) return null;
					const categoryIndex = api.value(0);
					const start = api.coord([api.value(1), categoryIndex]);
					const end = api.coord([api.value(2), categoryIndex]);
					const height = api.size([0, 1])[1] * 0.3;

					const minTimeCoord = api.coord([TIME_START, categoryIndex]);
					const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
					const minX = minTimeCoord[0];
					const maxX = maxTimeCoord[0];

					const finalStartX = Math.max(start[0], minX);
					const finalEndX = Math.min(end[0], maxX);
					const barWidth = Math.max(finalEndX - finalStartX, 2);

					if (start[0] > maxX || end[0] < minX) return null;

					return {
						type: "rect",
						shape: {
							x: finalStartX,
							y: start[1] - height / 2,
							width: barWidth,
							height: height,
						},
						style: {
							fill: "#9E9E9E",
							stroke: "#757575",
							lineWidth: 1,
							opacity: 0.6,
						},
					};
				},
				encode: {
					x: [1, 2],
					y: 0,
					tooltip: [0, 1, 2],
				},
				data: gapTimeDataFormatted,
			},

			{
				name: "Adjustments",
				type: "custom",
				z: 6,
				renderItem: function (params: any, api: any) {
					const categoryIndex = api.value(0);
					const timestamp = api.coord([api.value(1), categoryIndex]);
					const radius = 7;

					const minTimeCoord = api.coord([TIME_START, categoryIndex]);
					const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
					const minX = minTimeCoord[0];
					const maxX = maxTimeCoord[0];

					if (timestamp[0] < minX || timestamp[0] > maxX) return null;

					return {
						type: "group",
						children: [
							{
								type: "circle",
								shape: {
									cx: timestamp[0],
									cy: timestamp[1],
									r: radius,
								},
								style: {
									fill: "#9C27B0",
									stroke: "#9C27B0",
									lineWidth: 1,
									opacity: 1,
								},
							},
						],
					};
				},
				encode: {
					x: [1],
					y: 0,
					tooltip: [0, 1],
				},
				data: adjustmentsData,
			},

			{
				name: "Pass Changes",
				type: "custom",
				z: 5,
				renderItem: function (params: any, api: any) {
					const categoryIndex = api.value(0);
					const timestamp = api.coord([api.value(1), categoryIndex]);
					const passNumber = passChangesData[params.dataIndex]?.passNumber;
					const radius = 7;

					const minTimeCoord = api.coord([TIME_START, categoryIndex]);
					const maxTimeCoord = api.coord([TIME_END, categoryIndex]);
					const minX = minTimeCoord[0];
					const maxX = maxTimeCoord[0];

					if (timestamp[0] < minX || timestamp[0] > maxX) return null;

					return {
						type: "group",
						children: [
							{
								type: "circle",
								shape: {
									cx: timestamp[0],
									cy: timestamp[1],
									r: radius,
								},
								style: {
									fill: "#00BCD4",
									stroke: "#00838F",
									lineWidth: 2,
									opacity: 1,
								},
							},
							{
								type: "text",
								style: {
									x: timestamp[0],
									y: timestamp[1],
									text: `${passNumber}`,
									textFill: "#fff",
									fontSize: 8,
									fontWeight: "bold",
									textAlign: "center",
									textVerticalAlign: "middle",
								},
							},
						],
					};
				},
				encode: {
					x: [1],
					y: 0,
					tooltip: [0, 1, 2],
				},
				data: passChangesData,
			},
		],
		graphic: [
			{
				type: "text",
				invisible: !dayLabelsInfo.startDay,
				left: 82,
				top: compactMode ? 2 : 1,
				style: {
					text: dayLabelsInfo.startDay?.label ?? "",
					fontSize: 11,
					fontWeight: "bold",
					fill: "#333",
					backgroundColor: "#fff",
					padding: [3, 8],
					borderRadius: 3,
				},
				z: 100,
			},
			{
				type: "text",
				invisible: !dayLabelsInfo.midnight,
				left: dayLabelsInfo.midnight
					? `${6 + dayLabelsInfo.midnight.percentage * 0.89}%`
					: "50%",
				top: compactMode ? 2 : 1,
				style: {
					text: dayLabelsInfo.midnight?.label ?? "",
					fontSize: 11,
					fontWeight: "bold",
					fill: "#333",
					backgroundColor: "#fff",
					padding: [3, 8],
					borderRadius: 3,
				},
				z: 100,
			},
		],
	};

	getMenuOptionsRef.current = getMenuOptions;
	showContextMenuRef.current = showContextMenu;

	useEffect(() => {
		registerConfig({
			id: "ganttContext",
			getOptions: getMenuOptionsRef.current,
		});
	}, []);

	return (
		<>
			<Grid2
				size={{ xs: 12, md: 12 }}
				style={{
					padding: 10,
					height: compactMode ? "350px" : "calc(105vh - 300px)",
					minHeight: compactMode ? "270px" : "calc(105vh - 300px)",
					margin: "10px 0",
				}}
				component={compactMode ? "div" : Paper}
				elevation={2}
			>
				{isLoading || isSubmitLoading ? (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
							width: "100%",
						}}
					>
						<CircularProgress />
					</div>
				) : (
					<ReactECharts
						option={option}
						notMerge={true}
						style={{ height: "100%" }}
						onChartReady={onChartReady}
						onEvents={GANTT_CHART_EVENTS}
					/>
				)}
			</Grid2>
			{!compactMode && (
				<>
					<CarrierInstallationModal
						show={
							dataToInstallCarrier !== null &&
							!showConfirmDialog &&
							!openPassChangeDialog
						}
						data={dataToInstallCarrier}
						onHide={async (update?: boolean) => {
							setDataToInstallCarrier(null);
							if (update) {
								await refetch();
							}
						}}
						suffixTitle="Gantt"
					/>
					<DeleteModal
						open={showConfirmDialog}
						onHide={async (update: boolean) => {
							setDataToInstallCarrier(null);
							setShowConfirmDialog(false);
							if (update) {
								await refetch();
							}
						}}
						carrierInstanceId={dataToInstallCarrier?.carrierInstanceId!}
						info={{
							partName: dataToInstallCarrier?.partName,
							sectionName: dataToInstallCarrier?.activeSection,
							standName: dataToInstallCarrier?.activeStand,
						}}
						suffixTitle="Gantt"
					/>
				</>
			)}
			<PassChangeModal
				open={openPassChangeDialog}
				onHide={async (updated?: boolean) => {
					if (updated) await refetch();
					setOpenPassChangeDialog(false);
					setDataToInstallCarrier(null);
				}}
				setNotificationMessage={setNotificationMessage}
				carrier={{
					partId: dataToInstallCarrier?.partId!,
					carrierInstanceId: dataToInstallCarrier?.carrierInstanceId!,
				}}
				initialTimestamp={
					dataToInstallCarrier?.clickedTime
						? new Date(dataToInstallCarrier.clickedTime)
						: undefined
				}
				refetch={refetch}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
